import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { useIsQuickshipAllowed } from './use-seller-setting';
import { UserSettings } from '../enums/user-settings/user-settings';
import { UserSettingsOptions, V2_ORDERS_PATH } from '../enums/user-settings/user-settings-options';
import { getUserSettingsOptions } from '../services/settings/user-settings-service';
import Global from '../store/global-selectors';
import { saveUserSetting } from '../store/user/user.actions';

export const useUserSetting = (userSettingName) => {
  return useSelector((state) => Global.selectUserUserSetting(state, { userSetting: userSettingName }));
};

const getValueOrDefault = (value, setting, defaultValue) => {
  const possibleValues = getUserSettingsOptions(setting);

  if (!!value && possibleValues.some(({ settingValue }) => settingValue === value)) {
    return value;
  }

  return defaultValue;
};

export const getParsedIntValueOrDefault = (settingValue, defaultValue) => {
  const parsedInt = Number.parseInt(settingValue, 10);
  if (Number.isNaN(parsedInt)) {
    return defaultValue;
  }
  return parsedInt;
};

const getParsedValueOrDefault = (settingValue, setting, defaultValue) => {
  const possibleValues = getUserSettingsOptions(setting);

  try {
    const parsedValue = JSON.parse(settingValue);
    if (parsedValue !== undefined && possibleValues.some((possible) => possible.settingValue === parsedValue)) {
      return parsedValue;
    }
  } catch {
    // do nothing
  }
  return defaultValue;
};

const useUserSettingWithDefault = (setting, defaultValue) => {
  const settingValue = useUserSetting(setting.settingName);
  return getParsedValueOrDefault(settingValue, setting, defaultValue);
};

export const useUserSettingsReady = () => {
  return useSelector(Global.selectUserUserSettingsReady);
};

export const useDefaultActionOptionSetting = () =>
  getValueOrDefault(
    useUserSetting(UserSettings.defaultButtonAction.settingName),
    UserSettings.defaultButtonAction,
    UserSettingsOptions.createAndPrintLabel.settingValue,
  );

export const useVerifyItemsBySetting = () => {
  const value = useUserSetting(UserSettings.verifyItemsBy.settingName);
  const possibleValues = getUserSettingsOptions(UserSettings.verifyItemsBy);

  try {
    const parsedValue = JSON.parse(value); // should be an array of strings
    if (
      Array.isArray(parsedValue) &&
      (parsedValue.length === 0 || possibleValues.some(({ settingValue }) => parsedValue.includes(settingValue)))
    ) {
      return parsedValue;
    }
  } catch {
    // do nothing
  }
  return [UserSettingsOptions.upc.settingValue];
};

export const useRequiredManualConfirmationSetting = () => {
  const value = useUserSetting(UserSettings.requiredManualConfirmation.settingName);
  const possibleValues = getUserSettingsOptions(UserSettings.requiredManualConfirmation);

  try {
    const parsedValue = JSON.parse(value);
    if (parsedValue !== undefined && possibleValues.some(({ settingValue }) => settingValue === parsedValue)) {
      return parsedValue;
    }
  } catch {
    // do nothing
  }
  return UserSettingsOptions.makeUsersClickMarkAsVerified.settingValue;
};

export const useUITimeFormatSetting = () =>
  getValueOrDefault(
    useUserSetting(UserSettings.uiTimeFormat.settingName),
    UserSettings.uiTimeFormat,
    UserSettingsOptions.twelveHour.settingValue,
  );

export const useUIDateFormatSetting = () =>
  getValueOrDefault(
    useUserSetting(UserSettings.uiDateFormat.settingName),
    UserSettings.uiDateFormat,
    UserSettingsOptions.monthDayYearLong.settingValue,
  );

export const useWeeklyDigestSetting = () => useUserSettingWithDefault(UserSettings.weeklyDigest, true);

export const useIsQuickshipEnabled = () => {
  const isQuickshipAllowedForSeller = useIsQuickshipAllowed();
  const isQuickshipEnabledForUser = useUserSettingWithDefault(UserSettings.isQuickshipEnabled, false);
  return isQuickshipAllowedForSeller && isQuickshipEnabledForUser;
};

export const useAdvancedHotkeysSetting = () => useUserSettingWithDefault(UserSettings.advancedHotkeys, true);

// the boolean quickship settings can just return true if QS isn't enabled
export const useQuickshipShowWarnings = () => useUserSettingWithDefault(UserSettings.quickshipShowWarnings, false);

export const useQuickshipShowErrors = () => useUserSettingWithDefault(UserSettings.quickshipShowErrors, false);
export const useQuickshipShowCostSummary = () =>
  useUserSettingWithDefault(UserSettings.quickshipShowCostSummary, UserSettingsOptions.quickshipNeverShowCostSummary.settingValue);
export const useQuickshipShowPrintDialogPreview = () => useUserSettingWithDefault(UserSettings.quickshipShowPrintDialogPreview, false);
export const useQuickshipShowLabelBatchStatus = () => useUserSettingWithDefault(UserSettings.quickshipShowLabelBatchStatus, false);

const DEFAULT_SLA_FILL_TIME_HOURS = 48;
export const useFillTimeSla = () =>
  getParsedIntValueOrDefault(useUserSetting(UserSettings.fillTimeSla.settingName), DEFAULT_SLA_FILL_TIME_HOURS);

export const useOnToggleQuickship = () => {
  const dispatch = useDispatch();
  const isQuickshipEnabled = useIsQuickshipEnabled();

  return useCallback(() => {
    dispatch(
      saveUserSetting({
        settingName: UserSettings.isQuickshipEnabled.settingName,
        settingValue: !isQuickshipEnabled,
        excludeV3prefix: true,
      }),
    );
  }, [dispatch, isQuickshipEnabled]);
};

export const useDefaultLoginRoute = () =>
  getValueOrDefault(useUserSetting(UserSettings.defaultUrl.settingName), UserSettings.defaultUrl, V2_ORDERS_PATH);

export const useHideCustomerSummaryPopover = () => useUserSettingWithDefault(UserSettings.hideCustomerSummaryPopover, false);
export const useHideExternallyChangedOrders = () => useUserSettingWithDefault(UserSettings.hideExternallyChangedOrders, false);
export const useShouldPersistCombineOrderSelection = () =>
  useUserSettingWithDefault(UserSettings.shouldPersistCombineOrderSelection, false);
export const useUspsNsaCopOnboardingCompleted = () => useUserSettingWithDefault(UserSettings.uspsNsaCopOnboardingCompleted, false);
export const useUspsNsaWaitingBannerDismissed = () => useUserSettingWithDefault(UserSettings.uspsNsaWaitingBannerDismissed, false);
export const useUspsNsaModalDismissedAtUTC = () => useUserSetting(UserSettings.uspsNsaModalDismissedAtUTC.settingName);
export const usePhoneNumberCaptureModalDismissed = () => useUserSettingWithDefault(UserSettings.phoneNumberCaptureModalDismissed, false);
export const usePhoneNumberCaptureBannerDismissed = () => useUserSettingWithDefault(UserSettings.phoneNumberCaptureBannerDismissed, false);
export const usePlanMigrationModalDismissed = () => useUserSettingWithDefault(UserSettings.planMigrationModalDismissed, false);

export const useCurrentUserId = () => useSelector((state) => Global.selectUserCurrentUserId(state));
export const useUserHasShipmentViewRestriction = () => useSelector((state) => Global.selectUseruserHasShipmentViewRestriction(state));
export const useUserHasShippingRole = () => useSelector((state) => Global.selectUserhasShippingRole(state));
